import React, { useEffect } from 'react';
import cookies from 'js-cookie';
import useSWRMutation from 'swr/mutation';
import { jwtDecode } from 'jwt-decode';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';

import { Button, PageLoader, Section } from '@cfm-media/pwa-ui-components';
import { useTranslation } from '../../core/utils/translations';

import { PageWrapper } from '../../components/page-wrapper';
import { dateFormatter } from '../../utils/date-formatter';
import { urls } from '../../urls';

export const ExcursionsStatus = observer(() => {
  const sessionId = window.location.search.split('session_id=')[1];
  const { t } = useTranslation(['status-page', 'activities-page']);
  const navigate = useNavigate();

  // Get Blank Booking Id from auth cookie to validate Stripe checkout session in RMS
  const { bookingId } = jwtDecode(cookies.get('app_token'));

  const {
    trigger: onCheckPaymentStatus,
    data: excursionBooking,
    isLoading,
    error,
  } = useSWRMutation('/rms/api/excursions/book/status', checkPaymentStatus);

  useEffect(() => {
    if (sessionId) {
      onCheckPaymentStatus({
        sessionId,
        blankBookingId: bookingId,
      });
    }
  }, []);

  if (isLoading || !excursionBooking?.status || !excursionBooking?.data) {
    return (
      <PageLoader variant="absolute" info="Buchungsformular wird geladen..." />
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <Section>
          <div className="flex flex-col items-center justify-center w-full min-h-full">
            <div className="flex flex-col w-full justify-center items-center">
              <div className="flex items-center justify-center w-20 h-20 bg-brand-1 text-white rounded-full my-3">
                <span className="icon icon-close text-2xl font-bold" />
              </div>
            </div>
            <h2 className="font-semibold text-lg mt-3 mb-2">
              {t('status-error-title')}
            </h2>
            <p> {t('status-error-text')}</p>
            <Button
              variant="primary"
              title="Zur Startseite"
              link={urls.home}
              className="w-auto mt-5"
            />
          </div>
        </Section>
      </PageWrapper>
    );
  }

  // TODO: fallback UI when we dont have a session_id
  // TODO: display booking summary from bookingForm

  return (
    <PageWrapper>
      <Section>
        <div className="flex flex-col w-full justify-center items-center mb-3">
          <div className="flex items-center justify-center w-20 h-20 bg-brand-2 text-white rounded-full my-3">
            <span className="icon icon-check text-2xl font-bold" />
          </div>
          <h2 className="font-semibold text-xl mb-5">
            {t('status-success-text')}
          </h2>
        </div>
        <div className="p-4 rounded-md border shadow-md text-sm bg-white">
          <h2 className="font-semibold text-lg mb-3 text-brand-1">
            {t('status-booking-info-text')}
          </h2>
          <div className="mb-1">
            <span className="font-semibold pr-2">
              {' '}
              {`${t('status-booking-text')}:`}
            </span>
            <span>{excursionBooking?.data?.title}</span>
          </div>
          <div className="mb-1">
            <span className="font-semibold pr-2">{`${t(
              'status-name-text',
            )}:`}</span>
            <span>{excursionBooking?.data?.clientName}</span>
          </div>
          <div className="mb-1">
            <span className="font-semibold pr-2">{`${t(
              'status-tele-text',
            )}:`}</span>
            <span>{excursionBooking?.data?.clientPhone}</span>
          </div>
          <div className="mb-1">
            <span className="font-semibold pr-2">{`${t(
              'status-booking-id-text',
            )}:`}</span>
            <span>{excursionBooking?.data?.blankBookingId}</span>
          </div>
          <div className="mb-1">
            <span className="font-semibold pr-2">{`${t(
              'status-booking-hotel-text',
            )}:`}</span>
            <span>{excursionBooking?.data?.blankHotelName}</span>
          </div>
          <div className="mb-1">
            <span className="font-semibold pr-2">{`${t(
              'status-booking-date-text',
            )}:`}</span>
            <span>{dateFormatter(excursionBooking?.data?.bookingDate)}</span>
          </div>
          <div className="mb-1">
            <span className="font-semibold pr-2">{`${t(
              'status-excursion-date',
            )}:`}</span>
            <span>{dateFormatter(excursionBooking?.data?.excursionDate)}</span>
          </div>
          <div className="mb-1">
            <span className="font-semibold pr-2">
              {`${t('status-label-adult')}:`}
            </span>
            <span>{excursionBooking?.data?.adults}</span>
          </div>
          <div className="mb-1">
            <span className="font-semibold pr-2">
              {`${t('status-label-child')}:`}
            </span>
            <span>{excursionBooking?.data?.children}</span>
          </div>
          <div className="mb-1">
            <span className="font-semibold pr-2">
              {`${t('status-label-infant')}:`}
            </span>
            <span>{excursionBooking?.data?.infants}</span>
          </div>
          <div className="mb-1">
            <span className="font-semibold pr-2">
              {`${t('status-label-price')}:`}
            </span>
            <span>
              {parseInt(excursionBooking?.data?.bookingPrice) / 100} €
            </span>
          </div>
        </div>
        <p className="mt-5 text-sm">
          Wir danken Ihnen für die Buchung. Sie erhalten in Kürze eine
          Bestätigung per E-Mail an Ihre Adresse{' '}
          {excursionBooking?.data?.clientEmail}. Einen Tag vor Ihrem Ausflug
          teilen wir Ihnen die genaue Abholzeit mit. Bei Rückfragen wenden Sie
          sich bitte an die{' '}
          <span
            className="underline hover:no-underline"
            onClick={navigate.bind(null, urls.contact)}
          >
            Reiseleitung
          </span>
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 mt-5">
          <Button
            title="Zurück"
            icon="icon-arrow-left"
            variant="primary"
            onButtonClick={navigate.bind(null, urls.excursions.index)}
          />
          <Button
            icon="icon-home"
            title="Zur Startseite"
            variant="primary"
            onButtonClick={navigate.bind(null, urls.home)}
          />
        </div>
      </Section>
    </PageWrapper>
  );
});

async function checkPaymentStatus(url, { arg: data }) {
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
    .then((res) => res.json())
    .then((data) => ({ status: data.status, data: data.data }))
    .catch((err) => console.error(err));
}

export default ExcursionsStatus;

// TODO: extract error view to a separate component
